import { useState } from "react";
import { Sheet, SheetContent } from "@/v2-components/ui/sheet";
import type { CartTrigger } from "@/libs/analytics/cart-trigger";
import { SwitchCase } from "@/v2-components/switch-case";
import { useMedia } from "react-use";
import { useForm } from "react-hook-form";
import { Product, ProductSlug } from "@/api/product-api";
import { CartService } from "@/services/cart-service";
import { useAddItem } from "@/store/cart-store";
// ⚠️ 정의 모듈에서 직접 — 배럴(`@/store/cart-store`)을 통째 모킹하는 테스트가 있어
// 배럴 경유로 쓰면 이 enum 이 `undefined` 가 되고 런타임에 터진다.
import { AddToCartResult } from "@/store/cart-store/type";
import { useAddedToBagStore } from "@/store/cart-store/useAddedToBagStore";
import { Button } from "@/v2-components/ui/button";
import { Form } from "@/v2-components/ui/form";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import {
  SheetClose,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/v2-components/ui/sheet";
import { useCurrency } from "@/hooks/useCurrency";
import { GePrice } from "@/v2-components/GePrice";
import type { SelectOptionFormData } from "@/v2-components/cart-sheet/types";
import { QuantityCounter } from "@/v2-components/cart-sheet/quantity-counter";
import { AccessoryList } from "@/v2-components/cart-sheet/accessory-list";
import { TactGloveOptions } from "@/v2-components/cart-sheet/TactGloveOptions";
import { Spinner } from "@/v2-components/ui/spinner";
import { useTrans } from "@/utils/useTrans";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { useProducts } from "@/services/product/useProductService";
import { useProductPrice } from "@/hooks/useProductPrice";
import { ProductUtil } from "@/utils/ProductUtil";
import { CartUtil } from "@/utils/CartUtil";
import { useLocale } from "@/store/locale-store";
import { ColorOptions } from "@/v2-components/cart-sheet/ColorOptions";

export function SelectProductOption({
  product,
  onClose,
  trigger = "unknown",
  notifyOnAdd = true,
}: {
  product: Product;
  onClose: () => void;
  /**
   * `add_to_cart` 의 진입점 (#5292). **표면이 정한다** — 이 시트는 제품 카드와 `/cart`
   * 추천 레일이 **함께** 쓴다. 여기서 하나로 못 박으면 다른 쪽이 틀린 라벨로 나간다.
   */
  trigger?: CartTrigger;
  /**
   * 담기 완료 팝(#5366)을 띄울 표면인가 — **표면이 정한다**(기본 `true`).
   * `trigger` 값으로 갈라선 안 된다(`cart-trigger-threading` 가드: 공유 계층이 값을 알면
   * 여러 표면 중 한쪽이 반드시 틀린다). 호출처 `useAddItemToCart` 가 그대로 내려준다.
   */
  notifyOnAdd?: boolean;
}) {
  const isMobile = useMedia("(max-width: 1023px)", false);
  const { t } = useTrans(["gnb", "checkout"]);
  const currency = useCurrency();
  const { countryCode } = useLocale();
  const products = useProducts();
  const addItem = useAddItem();
  const openAddedToBag = useAddedToBagStore((s) => s.open);
  // 본품 SKU 를 못 찾은 상태 — 조용히 넘기지 않고 드러낸다 (#5286)
  const [skuMissing, setSkuMissing] = useState(false);
  const { getProductBySlug, getProductPrice } = useCartWithProducts();

  const form = useForm<SelectOptionFormData>({
    defaultValues: {
      options: {}, // variant options
      quantity: 1, // main product quantity
      additionalProducts: [], // accessories
    },
  });

  const isAllSizeGlove =
    CartUtil.isGloveDevice(product.slug) &&
    form.watch().options.size === "all-size";

  // 글러브 사이즈 세트(all-size)는 자기 번들 slug 로 전환 (DK2→dk2-bundle, DK3→dk3-bundle).
  const bundleSlug = CartUtil.getGloveBundleSlug(product.slug);

  const productSlug = isAllSizeGlove && bundleSlug ? bundleSlug : product.slug;

  const _product = getProductBySlug(productSlug);
  const price = getProductPrice(_product);

  // 글러브(DK2/DK3)는 동일 컴포넌트 — slug 로 분기되므로 caseBy 케이스별 중복 불필요.
  const gloveOptions = _product ? (
    <TactGloveOptions
      skuOptions={_product.skuOptions}
      productSlug={_product.slug}
    />
  ) : null;

  const { displayOriginalPrice, displayDiscountAmount, displaySalePrice } =
    useProductPrice(price);

  const additionalPrice = form.watch("additionalProducts").reduce(
    (acc, product) => {
      const additional = getProductBySlug(product.slug);

      // ★render 중 throw 금지 — 여기는 `reduce` 안이고 렌더 경로다. 던지면 카트시트 전체가
      // 언마운트되어 **화이트스크린**이 된다. 담긴 다른 상품까지 못 보게 되는 건 과잉 대응이다.
      // 값을 못 구한 항목은 합계에서 빼고 넘어간다(라인 skip). 원인은 로그로 남긴다.
      if (!additional) {
        console.warn(
          `[cart] additional product not found: ${product.slug} — 합계에서 제외`,
        );
        return acc;
      }

      // ⚠️ `prices`(기준가 배열)를 직접 넘기면 국가별 티어(usPrice/euPrice/gbPrice)를 건너뛴다.
      // 반드시 `getProductPrice`(= getCountryAwarePrices 경유)를 쓸 것. docs/pricing-tiers.md §3.2
      const price = ProductUtil.getProductPrice({
        currency,
        countryCode,
        product: additional,
      });

      // 해당 통화 가격 미등록(EU/GB 에서 `euPrice`/`gbPrice` 가 0 이면 발생). prod 는 현재
      // 36/36 이 0 이라 flag ON 시 실제로 도달한다 — 종전엔 여기서 throw 라 화이트스크린이었다.
      if (!price) {
        console.warn(
          `[cart] no ${currency} price for ${product.slug} — 합계에서 제외`,
        );
        return acc;
      }

      return {
        displayTotalSalePrice:
          acc.displayTotalSalePrice +
          (price.originalAmount - price.discountAmount) * product.quantity,
        displayTotalOriginalPrice:
          acc.displayTotalOriginalPrice +
          price.displayAmount * product.quantity,
      };
    },
    { displayTotalSalePrice: 0, displayTotalOriginalPrice: 0 },
  );

  const displayTotalSalePrice =
    displaySalePrice * form.watch("quantity") +
    additionalPrice.displayTotalSalePrice;
  const displayTotalOriginalPrice =
    displayOriginalPrice * form.watch("quantity") +
    additionalPrice.displayTotalOriginalPrice;

  function onSubmit(formData: SelectOptionFormData) {
    if (!_product) return;
    const slug = isAllSizeGlove && bundleSlug ? bundleSlug : _product.slug;
    const quantity = formData.quantity;
    const options = Object.entries(formData.options).map(([type, value]) => ({
      type,
      value,
    }));

    setSkuMissing(false);

    const sku = ProductUtil.getSkuByOptions({
      product: _product,
      options,
      gameResells: [],
    });

    /**
     * ★`sku` 가 없으면 **아무것도 담지 않고 멈춘다** (#5286).
     *
     * 종전에는 `if (sku)` 가 거짓이어도 아래 액세서리 루프가 그대로 돌고 `onClose()` 까지
     * 갔다 — **본품만 조용히 빠진 채 다이얼로그가 정상적으로 닫혔다.** 사용자에게는 오류가
     * 없고 결제까지 갈 수 있다. 이 설계에서 유일하게 무음인 실패였다.
     *
     * 알려진 원인은 게임키 skuOption(위 `addCartBySlug` 에서 PDP 로 우회시킨다)이지만, 이
     * 가드는 **원인과 무관하게** 부분 담기를 막는다 — 미래에 다른 이유로 매칭이 깨져도
     * 무음이 되지 않는다.
     */
    if (!sku) {
      setSkuMissing(true);
      return;
    }

    const result = addItem(
      {
        slug,
        quantity,
        options,
        sku,
      },
      true,
      trigger,
    );

    formData.additionalProducts.forEach((product) => {
      addItem(
        {
          slug: product.slug,
          quantity: product.quantity,
          options: product.options,
          sku: product.sku,
        },
        true,
        trigger,
      );
    });
    onClose();

    /**
     * ★담기 완료 팝 (#5366 리뷰 R1).
     *
     * 이 시트는 **담기 전** UI 다 — 닫히고 나면 종전에 아무 신호가 없었다. 옵션이 있는
     * 제품(`skuOptions.length > 1`)은 전부 이 경로이고, prod 실측으로 **액세서리 7종**
     * (배지)이 여기 해당한다. 사용자는 옵션 유무를 모르고 둘 다 "액세서리 담기" 라서,
     * 옵션 없는 경로만 고치면 이슈가 말한 무음이 **절반만** 해소된다.
     *
     * ⚠️ **`Success` 일 때만 띄운다** — `addCartBySlug` 와 같은 판정이다. `addItem` 은 세
     * 갈래이고 둘은 이미 자기 표면이 있다(`OverLimit`=Purchase Limit 다이얼로그 ·
     * `MergedToBundle`=`/cart` 이동). 반환을 안 보면 그 위에 "담았다" 를 겹쳐 **사실과
     * 다른 말을 한다.**
     *
     * ⚠️ 판정은 **본품**의 결과로 한다 — 팝은 방금 담은 그 항목 하나를 보여주고, 함께
     * 담기는 `additionalProducts` 는 그 항목이 아니다.
     */
    if (notifyOnAdd && result === AddToCartResult.Success) {
      openAddedToBag(sku);
    }
  }

  return (
    <>
      <Sheet
        open={true}
        onOpenChange={() => {
          onClose();
        }}
      >
        <SheetContent
          className="flex flex-col laptop:min-w-[634px]"
          side={isMobile ? "bottom" : "right"}
        >
          <SheetHeader className="items-center justify-between">
            <SheetTitle>
              {t(`Select {{product-name}} Options`, {
                "product-name": _product?.name,
              })}
            </SheetTitle>
            <SheetDescription className="sr-only">
              Select the options you want and add to bag
            </SheetDescription>
            <SheetClose className="focus:outline-none">
              <CrossIcon />
              <span className="sr-only">Close</span>
            </SheetClose>
          </SheetHeader>

          {!_product || products.isPending ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <Form {...form}>
              <form
                className="-mx-4 flex flex-1 flex-col overflow-y-auto px-4 pt-5 laptop:-mx-8 laptop:px-8"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                <section className="mb-[150px] flex-1">
                  <div className="flex flex-col">
                    <SwitchCase
                      value={_product.slug}
                      caseBy={{
                        [ProductSlug.TACTGLOVE_DK2]: gloveOptions,
                        [ProductSlug.TACTGLOVE_DK3]: gloveOptions,
                      }}
                      defaultComponent={
                        <ColorOptions skuOptions={_product.skuOptions} />
                      }
                    />
                  </div>

                  <div
                    className="mt-3 flex items-start justify-between"
                    id="quantity"
                  >
                    <div className="space-y-2">
                      <h3 className="text-2xl">{t("Quantity")}</h3>
                      <p className="text-sm text-coolgray-60">
                        {t(`* You can buy up to 4 items`)}
                      </p>
                    </div>
                    <QuantityCounter
                      value={form.getValues("quantity")}
                      decreaseDisabled={form.getValues("quantity") <= 1}
                      increaseDisabled={form.getValues("quantity") >= 4}
                      onDecrease={() =>
                        form.setValue(
                          "quantity",
                          form.getValues("quantity") - 1,
                        )
                      }
                      onIncrease={() =>
                        form.setValue(
                          "quantity",
                          form.getValues("quantity") + 1,
                        )
                      }
                    />
                  </div>

                  <div
                    className="mt-6 flex flex-col justify-between pt-3 laptop:mt-7"
                    id="accessories"
                  >
                    <AccessoryList
                      slug={_product.slug}
                      packageDeals={_product.packageDeals as ProductSlug[]}
                      additionalProducts={form.watch("additionalProducts")}
                      updateAdditionalProducts={(products) => {
                        form.setValue("additionalProducts", products);
                      }}
                    />
                  </div>
                </section>

                <section className="absolute bottom-0 -mx-4 flex w-full flex-col gap-2 border-t border-coolgray-20 bg-white p-4 pb-5 laptop:-mx-8 laptop:p-8">
                  {/* 본품 SKU 매칭 실패 — 조용히 닫히는 대신 여기서 멈춘다 (#5286).
                      새 번역 키를 만들지 않고 7 로케일에 이미 있는 `checkout` 의 문구를
                      쓴다(namespace 를 추가했다). */}
                  {skuMissing && (
                    <p className="text-sm text-red-60" role="alert">
                      {t("Something went wrong", { ns: "checkout" })}
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="blue-fill"
                    shape="pill"
                    type="submit"
                    disabled={
                      !CartService.isAllVariantOptionsSelected({
                        product: _product,
                        options: Object.entries(form.watch("options")).map(
                          ([type, value]) => ({ type, value }),
                        ),
                      })
                    }
                    className="flex justify-between px-4 py-3 laptop:px-5 laptop:py-4 laptop:text-lg"
                  >
                    <span className="font-600 laptop:text-base">
                      {t(`Add to Bag`)}
                    </span>
                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                      <div className="flex items-center justify-end gap-1">
                        <p className="text-lg font-600 laptop:text-2xl">
                          <GePrice
                            amount={displayTotalSalePrice}
                            currency={price?.currency}
                            countryCode={countryCode}
                          />
                        </p>
                        {!!displayDiscountAmount && (
                          <p className="text-sm font-400 line-through laptop:text-lg">
                            <GePrice
                              amount={displayTotalOriginalPrice}
                              currency={price?.currency}
                              countryCode={countryCode}
                            />
                          </p>
                        )}
                      </div>
                    </div>
                  </Button>
                </section>
              </form>
            </Form>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
